import { HOVER_INTENT_DELAY } from '@/js/consts.js';

class MegaMenuItem extends HTMLElement {
  connectedCallback() {
    this.initVariables();
    this.initEventListeners();
    this.touchDeviceAdjustments();
    this.scrollMenuToTop();
    this.closePredictiveSearchBar();
  }

  initVariables() {
    this.menuItems = document.querySelectorAll('[data-mega-menu-item]');
    this.megaMenus = document.querySelectorAll('[data-mega-menu]');
    this.topLevelLink = this.querySelector('[data-mega-menu-link]');
    this.predictiveSearchBar = document.querySelector(
      '[data-predictive-search-bar]'
    );
    this.predictiveSearchResults = document.querySelector(
      '[data-predictive-search-results]'
    );
    this.tabletMediaQuery = globalThis.matchMedia('(max-width: 1024px)');
    this.isTablet =
      /ipad/i.test(navigator.userAgent) || this.tabletMediaQuery.matches;
    // Hover intent state
    this.hoverIntentTimer = undefined;
    this.isHovering = false;
  }

  initEventListeners() {
    // On mouseenter, start a timer to detect true hover intent before opening submenu
    this.addEventListener('mouseenter', event => {
      if (!this.isTablet) {
        // Clear any previous timer in case of rapid re-entry
        clearTimeout(this.hoverIntentTimer);
        // Start hover intent timer: only open submenu if mouse stays for 200ms
        this.hoverIntentTimer = setTimeout(() => {
          this.isHovering = true;
          this.toggleSubMenu(event);
        }, HOVER_INTENT_DELAY);
      }
      this.closePredictiveSearchBar();
      this.closePredictiveSearchResults();
    });

    // On mouseleave, clear the timer so submenu does not open if mouse leaves early
    this.addEventListener('mouseleave', () => {
      clearTimeout(this.hoverIntentTimer);
      this.hoverIntentTimer = undefined;
      this.isHovering = false;
    });
  }

  /**
   * Add the active class to the menu item
   * @returns {void}
   */
  updateActiveMenuItem() {
    // Also add another class to help with styling for the mega-menu-item
    this.classList.add('active');

    for (const menuItem of this.menuItems) {
      if (menuItem !== this) {
        menuItem.classList.remove('active');
      }
    }
  }

  /**
   * Toggle the submenu
   * @param {object} event
   * @returns
   */
  toggleSubMenu(event) {
    if (!this.isHovering) return; // Only allow if hover intent is established
    this.megaMenuItemName = event.target.dataset.megaMenuItem;
    this.menu = document.querySelector('[data-menu]');

    // Remove hidden class
    const subMenu = document.querySelector(
      `[data-mega-menu="${this.megaMenuItemName}"]`
    );

    // Hide all other submenus apart from the one we are hovering over
    for (const menu of this.megaMenus) {
      if (menu !== subMenu) {
        menu.classList.remove('active');
      }
    }

    this.updateActiveMenuItem(event);
    // If there is no submenu, remove the active class from the menu item
    if (!subMenu) {
      this.classList.remove('active');
      this.menu.classList.remove('parent-active');
      return;
    }

    // Add active class to both the menu item and the submenu
    subMenu.classList.add('active');

    const space6withUnit = globalThis
      .getComputedStyle(subMenu)
      .getPropertyValue('--space-6');
    const space6 = Number.parseFloat(space6withUnit) * 2 * 16; // padding top and bottom (*2) and convert to px (* 16)
    subMenu.style.setProperty(
      '--max-height',
      `${subMenu.scrollHeight + space6}px`
    );

    for (const menuItem of this.menuItems) {
      if (menuItem.classList.contains('active')) {
        this.menu.classList.add('parent-active');
      }
    }
  }

  /**
   * For touch devices, prevent the to level links from navigating on click. Instead, clicking will just open the mega menu.
   * @returns {void}
   */
  touchDeviceAdjustments() {
    const touchDeviceQuery = globalThis.matchMedia('pointer: coarse');
    const isTouchDevice = touchDeviceQuery.matches;
    if (!isTouchDevice) {
      return;
    }

    this.topLevelLink.addEventListener('click', event => {
      event.preventDefault();
    });
  }

  /**
   * Scroll the menu to the top when a menu item is hovered
   * @returns {void}
   */
  scrollMenuToTop() {
    const menu = document.querySelector('[data-mega-menu-container');
    const menuItems = document.querySelectorAll('[data-mega-menu-item]');

    for (const menuItem of menuItems) {
      menuItem.addEventListener('mouseenter', () => {
        menu.scrollTop = 0;
      });
    }
  }

  /**
   * Close the predictive search bar
   * @returns {void}
   */
  closePredictiveSearchBar() {
    if (
      this.predictiveSearchBar &&
      this.predictiveSearchBar.classList.contains('search-results-open')
    ) {
      this.predictiveSearchBar.closeSearchBar();
    }
  }

  /**
   * Close the predictive search results
   */
  closePredictiveSearchResults() {
    // If the predictive search results have been expanded, also close out
    if (
      this.predictiveSearchResults &&
      this.predictiveSearchResults.getAttribute('aria-hidden') == 'false'
    ) {
      this.predictiveSearchResults.close();
    }
  }
}

customElements.get('mega-menu-item', MegaMenuItem) ||
  customElements.define('mega-menu-item', MegaMenuItem);
